// Budget detail: the plan's lines and periods with actual spend beside each
// one, so overspend shows as a negative remainder. Actuals come from posted
// journal lines. New lines and periods are added here; posted actuals stay
// read-only.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import {
  type BudgetDetail,
  type BudgetVsActualRow,
  addBudgetLine,
  addBudgetPeriod,
  getBudget,
  getBudgetVsActual,
} from "@/lib/api";
import { humanizeLabel, useAccountLabels } from "@/lib/labels";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function toCents(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const cents = Math.round(value * 100);
  return Number.isSafeInteger(cents) ? cents : null;
}

export function BudgetDetailPage() {
  const { budgetId } = useParams({ strict: false }) as {
    budgetId: string;
  };
  const [detail, setDetail] = useState<BudgetDetail | null>(null);
  const [report, setReport] = useState<BudgetVsActualRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [lineOpen, setLineOpen] = useState(false);
  const [periodLineId, setPeriodLineId] = useState<string | null>(null);
  const accountLabels = useAccountLabels();

  const reload = useCallback(() => {
    if (!budgetId) return;
    setError(null);
    setReportError(null);
    setReport(null);
    getBudget(budgetId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
    getBudgetVsActual(budgetId)
      .then(setReport)
      .catch((cause: unknown) => setReportError(toErrorMessage(cause)));
  }, [budgetId]);

  useEffect(reload, [reload]);

  const actualByPeriod = new Map(
    (report ?? []).map((row) => [row.budget_period_id, row]),
  );

  const totals = (report ?? []).reduce(
    (sum, row) => ({
      budgeted: sum.budgeted + row.budgeted_cents,
      actual: sum.actual + row.actual_cents,
    }),
    { budgeted: 0, actual: 0 },
  );

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/settings/budgets"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Budgets
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.budget.name ?? budgetId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.budget.name}
            description={
              <>
                {new Date(detail.budget.start_date).toLocaleDateString()} →{" "}
                {new Date(detail.budget.end_date).toLocaleDateString()} ·{" "}
                {humanizeLabel(detail.budget.period_type)} ·{" "}
                <StatusDot
                  tone={detail.budget.is_active ? "info" : "idle"}
                  label={detail.budget.is_active ? "Active" : "Inactive"}
                />
              </>
            }
            actions={
              <Button type="button" size="sm" onClick={() => setLineOpen(true)}>
                Add account line
              </Button>
            }
          />

          {reportError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              Budget comparison unavailable: {reportError}
              <div className="mt-2">
                <Button type="button" variant="secondary" size="sm" onClick={reload}>
                  Retry comparison
                </Button>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Planned
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {report === null ? "—" : money(totals.budgeted)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Actually spent
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {report === null ? "—" : money(totals.actual)}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Remaining
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {report === null ? "—" : money(totals.budgeted - totals.actual)}
              </p>
            </div>
          </div>

          {detail.lines.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              This plan has no account lines yet.
            </p>
          ) : (
            detail.lines.map((line) => {
              const periods =
                detail.periods_by_line.find(
                  (group) => group.line_id === line.id,
                )?.periods ?? [];
              return (
                <section
                  key={line.id}
                  aria-label={`Plan for ${accountLabels.get(line.chart_account_id) ?? "account"}`}
                  className="grid gap-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2
                      className="text-lg font-semibold tracking-tight"
                      title={line.chart_account_id}
                    >
                      {accountLabels.get(line.chart_account_id) ?? "Account"}
                    </h2>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setPeriodLineId(line.id)}
                    >
                      Add period
                    </Button>
                  </div>
                  <div className="surface overflow-hidden rounded-2xl">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead className="text-right">Planned</TableHead>
                          <TableHead className="text-right">
                            Actually spent
                          </TableHead>
                          <TableHead className="text-right">
                            Remaining
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {periods.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4}>
                              No periods on this line yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          periods.map((period) => {
                            const actual = actualByPeriod.get(period.id);
                            const spent = actual?.actual_cents;
                            return (
                              <TableRow key={period.id}>
                                <TableCell className="whitespace-nowrap">
                                  {period.label}
                                  <span className="block text-xs text-[var(--muted-foreground)]">
                                    {new Date(
                                      period.start_date,
                                    ).toLocaleDateString()}{" "}
                                    →{" "}
                                    {new Date(
                                      period.end_date,
                                    ).toLocaleDateString()}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {money(period.amount_cents)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {spent === undefined ? "—" : money(spent)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {spent === undefined
                                    ? "—"
                                    : money(period.amount_cents - spent)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              );
            })
          )}
        </>
      ) : null}

      {lineOpen && budgetId ? (
        <AddLineDrawer
          budgetId={budgetId}
          onClose={() => setLineOpen(false)}
          onDone={reload}
        />
      ) : null}
      {periodLineId && budgetId && detail ? (
        <AddPeriodDrawer
          budgetId={budgetId}
          lineId={periodLineId}
          sortOrder={
            detail.periods_by_line.find((group) => group.line_id === periodLineId)
              ?.periods.length ?? 0
          }
          onClose={() => setPeriodLineId(null)}
          onDone={reload}
        />
      ) : null}
    </div>
  );
}

function AddLineDrawer({
  budgetId,
  onClose,
  onDone,
}: {
  budgetId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [accountId, setAccountId] = useState("");
  const [total, setTotal] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!accountId) throw new Error("Pick an account.");
      const total_cents = toCents(total);
      if (total_cents === null)
        throw new Error("Line total must be a zero or positive amount.");
      await addBudgetLine(budgetId, { chart_account_id: accountId, total_cents });
      onDone();
      onClose();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title="Add account line"
      description="The line total is the plan for this account. Periods split that plan by date."
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <form onSubmit={submit} className="grid gap-3">
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label htmlFor="budget-line-account" className="grid gap-1 text-xs font-medium">
          Account
          <AccountPicker
            id="budget-line-account"
            value={accountId}
            onChange={setAccountId}
            disabled={saving}
            required
          />
        </label>
        <label htmlFor="budget-line-total" className="grid gap-1 text-xs font-medium">
          Line total (USD)
          <Input
            id="budget-line-total"
            type="number"
            min="0"
            step="0.01"
            value={total}
            onChange={(event) => setTotal(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <Button type="submit" disabled={saving}>
          {saving ? "Adding…" : "Add line"}
        </Button>
      </form>
    </Drawer>
  );
}

function AddPeriodDrawer({
  budgetId,
  lineId,
  sortOrder,
  onClose,
  onDone,
}: {
  budgetId: string;
  lineId: string;
  sortOrder: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [label, setLabel] = useState("");
  const [startDate, setStartDate] = useState(todayInput);
  const [endDate, setEndDate] = useState(todayInput);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!label.trim()) throw new Error("Period label is required.");
      if (!startDate || !endDate)
        throw new Error("Start and end dates are required.");
      if (endDate < startDate)
        throw new Error("The end date must be on or after the start date.");
      const amount_cents = toCents(amount);
      if (amount_cents === null)
        throw new Error("Period amount must be a zero or positive amount.");
      await addBudgetPeriod(budgetId, lineId, {
        label: label.trim(),
        start_date: startDate,
        end_date: endDate,
        amount_cents,
        sort_order: sortOrder,
      });
      onDone();
      onClose();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title="Add period"
      description="Adds one dated slice of this account line. Actual spend still comes from posted journals."
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <form onSubmit={submit} className="grid gap-3">
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label htmlFor="budget-period-label" className="grid gap-1 text-xs font-medium">
          Label
          <Input
            id="budget-period-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label htmlFor="budget-period-start" className="grid gap-1 text-xs font-medium">
          Starts
          <Input
            id="budget-period-start"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label htmlFor="budget-period-end" className="grid gap-1 text-xs font-medium">
          Ends
          <Input
            id="budget-period-end"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label htmlFor="budget-period-amount" className="grid gap-1 text-xs font-medium">
          Amount (USD)
          <Input
            id="budget-period-amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <Button type="submit" disabled={saving}>
          {saving ? "Adding…" : "Add period"}
        </Button>
      </form>
    </Drawer>
  );
}
